import { useCallback, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { Question } from '../../../interfaces/question.interface';
import { AssessmentSurvey, AssessorAnswer, PIDetails } from '../../../interfaces/assessment.interface';
import { Survey } from '../../../interfaces/survey.interface';
import { saveFindingAtomic } from '../../../services/assessments';
import {
  NOT_IMPLEMENTED,
  PARTIALLY_IMPLEMENTED,
  REMEDIATION_OPTIONS,
  generateDefaultSubRemediationsObject,
} from '../../../services/surveyHelpers';
import { isWorkflowFiveThreeOrGreater } from '../../../services/questionnaires';
import { buildAssessorAnswer, getRemediationCounts } from '../../../utils/remediationUtils';
import { setAssessmentQuestionStatus, setAssessmentQuestionStatusId } from '../../../app/store/questionnaireAnswerSlice';
import {
  fetchAssessment as storeFetchAssessment,
  updateAssessmentAssessmentSurvey,
  updateAssessmentSurveyArrayAssessorAnswer,
} from '../../../app/store/assessmentSlice';
import { recordSessionSave } from '../../../app/store/assessmentWorkspaceSlice';

export interface SaveFindingInput {
  question: Question;
  assessorAnswer: string;
  assessorFinding: string;
  questionCtrlAns: { [key: string]: PIDetails };
  tags?: string[];
}

export type SaveFindingResult = { ok: true; answerId?: number; tags?: string[] } | { ok: false; error: string };

// Click-driven workspace save (never from useEffect) — mirrors the engine's findings path (handleUpdateAssessorAnswer, savePIQA=true) so both routes persist identically.
export function useSaveFinding() {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(state => state.user);
  const { assessment } = useAppSelector(state => state.assessments);
  const { workflow } = useAppSelector(state => state.workflow);
  const { survey } = useAppSelector(state => state.surveys);
  const { assessmentQuestionsStatus, questionStatus, questions } = useAppSelector(state => state.questionnaireAnswer);
  const [isSaving, setIsSaving] = useState(false);

  const saveFinding = useCallback(async (input: SaveFindingInput): Promise<SaveFindingResult> => {
    const { question, assessorAnswer, assessorFinding } = input;
    if (!assessmentQuestionsStatus || !user || !assessment || !survey) return { ok: false, error: 'Missing assessment context' };
    if (!assessorAnswer || assessorFinding.length === 0) {
      return { ok: false, error: 'Assessor Findings are required' };
    }

    const isFiveThreeOrGreater = workflow?.workflowType ? isWorkflowFiveThreeOrGreater(workflow.workflowType) : false;

    if (assessorAnswer === PARTIALLY_IMPLEMENTED && isFiveThreeOrGreater) {
      const hasMissingPIDetails = Object.values(input.questionCtrlAns).some(value => !value.compensatingOrNotApplicable);
      if (hasMissingPIDetails) {
        return { ok: false, error: 'Please provide an answer for all Partially Implemented controls.' };
      }
    }

    setIsSaving(true);
    try {
      const existing = assessmentQuestionsStatus[question.id];

      // Not Implemented on 5.3+ auto-populates the default sub-remediation object.
      let questionCtrlAns = input.questionCtrlAns;
      if (isFiveThreeOrGreater && assessorAnswer === NOT_IMPLEMENTED && question.type && questionStatus[question.id]) {
        const questionAnswer = questionStatus[question.id].questionAnswer;
        if (!questionAnswer) return { ok: false, error: 'Missing service provider answer' };
        const defaults = generateDefaultSubRemediationsObject(question.answers, question.type, questionAnswer);
        Object.keys(defaults).forEach(key => {
          const answerId = Number(key);
          if (questionCtrlAns[answerId]?.id) defaults[answerId].id = questionCtrlAns[answerId].id;
        });
        questionCtrlAns = defaults;
      }

      // Status state machine — same rules as the engine save path.
      let status = existing?.status || questionStatus[question.id]?.status;
      if (status !== 'priority') {
        const isPIWithoutRemediationSubRem = assessorAnswer === PARTIALLY_IMPLEMENTED
          && isFiveThreeOrGreater
          && Object.values(questionCtrlAns).every(detail =>
            !['compensating_control', 'not_implemented'].includes(detail.compensatingOrNotApplicable || ''));
        status = (['Fully Implemented', 'Not Applicable'].indexOf(assessorAnswer) !== -1 || isPIWithoutRemediationSubRem)
          ? 'assessorApproved' : 'remediation';
      }
      if (status === 'priority' && ['Fully Implemented', 'Not Applicable'].indexOf(assessorAnswer) !== -1) status = 'assessorApproved';
      const vendorStatus = existing?.vendorStatus;
      if (vendorStatus === REMEDIATION_OPTIONS.remediated) status = 'submitted';


      const assessmentSurveyId = (assessment.surveys as AssessmentSurvey[]).find(assSur =>
        typeof assSur.survey === 'number' ? assSur.survey === survey.id : assSur.survey.id === survey.id)?.id;
      if (!assessmentSurveyId) return { ok: false, error: 'Assessment survey not found' };

      const surveyIndex = (assessment.surveys as AssessmentSurvey[]).findIndex(
        assSur => (assSur.survey as Survey).id === survey.id || assSur.survey === survey.id,
      );
      if (surveyIndex === -1) return { ok: false, error: 'Assessment survey not found' };

      const { bestPracticeRemediationCount, implementationGuidanceRemediationCount } = getRemediationCounts({
        assessmentQuestionsStatus, assessment, questionStatus, questions,
      });

      const existingComments = existing?.comments || [];
      const existingCommentIds = existingComments.map(comment => (typeof comment === 'number' ? comment : comment.id!));

      const builtAssessorAnswer = buildAssessorAnswer({
        assessment,
        assessmentQuestionsStatus,
        questionCtrlAns,
        assessorAnswer,
        assessorFinding,
        createdSubRemediations: undefined,
        data: undefined,
        existingCommentIds,
        prop: { status, auditLog: undefined, savePIQA: true },
        question,
        remediationComment: existing?.remediationComment,
        remediationDate: existing?.remediationDate ?? null,
        survey,
        vendorStatus,
        willNotRemediateReason: existing?.willNotRemediateReason,
      });

      const saved = await saveFindingAtomic({
        question: question.id,
        assessmentSurvey: assessmentSurveyId,
        answer: assessorAnswer,
        assessorFinding,
        status: status || '',
        vendorStatus: vendorStatus ?? null,
        remediationComment: existing?.remediationComment || '',
        remediationDate: existing?.remediationDate ?? null,
        willNotRemediateReason: existing?.willNotRemediateReason ?? null,
        subAnswers: (builtAssessorAnswer.assessorQuestionAnswers || []).map(aqa => ({
          answer: aqa.answer as number,
          comment: aqa.comment || '',
          isImplemented: aqa.isImplemented || false,
          compensatingOrNotApplicable: aqa.compensatingOrNotApplicable || null,
        })),
        tags: input.tags,
        bestPracticeRemediationCount,
        implementationGuidanceRemediationCount,
        directLink: window.location.origin + window.location.pathname
          + (window.location.pathname[window.location.pathname.length - 1] !== '/' ? '/' : '')
          + '?question=' + question.id,
      });

      // Store updates mirror the response ids; shapes stay the locally built ones.
      builtAssessorAnswer.assessorQuestionAnswers = builtAssessorAnswer.assessorQuestionAnswers?.map(aqa => ({
        ...aqa,
        id: saved.assessorQuestionAnswers.find(sub => sub.answer === aqa.answer)?.id ?? aqa.id,
        assessorAnswer: saved.assessorAnswerId,
      }));
      const savedTags = input.tags ?? existing?.tags ?? [];
      const persistedAnswer = {
        ...builtAssessorAnswer,
        id: saved.assessorAnswerId,
        question: question.id,
        comments: existing?.comments || [],
        tags: savedTags,
      } as AssessorAnswer;

      if (saved.created) {
        dispatch(setAssessmentQuestionStatusId({ ...persistedAnswer, question }));
      }
      dispatch(updateAssessmentSurveyArrayAssessorAnswer(persistedAnswer));

      const newSurvey = { ...(assessment.surveys as AssessmentSurvey[])[surveyIndex] };
      dispatch(updateAssessmentAssessmentSurvey({
        ...newSurvey, bestPracticeRemediationCount, implementationGuidanceRemediationCount,
      }));
      dispatch(setAssessmentQuestionStatus({ ...builtAssessorAnswer, id: saved.assessorAnswerId, tags: savedTags }));

      if (saved.deletedNotification && assessment.id) {
        dispatch(storeFetchAssessment(assessment.id));
      }

      dispatch(recordSessionSave({
        questionId: question.id,
        by: [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email || 'Assessor',
        at: Date.now(),
      }));
      // The saved answer's identity rides the result so batch callers don't re-read a stale store closure.
      return { ok: true, answerId: saved.assessorAnswerId, tags: savedTags };
    } catch (error) {
      console.error('Error updating assessor answer:', error);
      return { ok: false, error: 'An error occurred while saving. Please try again.' };
    } finally {
      setIsSaving(false);
    }
  }, [dispatch, user, assessment, survey, workflow?.workflowType, assessmentQuestionsStatus, questionStatus, questions]);

  return { saveFinding, isSaving };
}
